import { Element } from "@nkitaa/builder-engine";
import { motion } from "framer-motion";
import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import type { BorderSide, RuntimePropsWithChildren } from "./shared";
import {
  CARD_HOVER_PROPS,
  SECTION_REVEAL_PROPS,
  ThemeSectionContainer,
  classNames,
  extractUtilityClasses,
  getBorderClasses,
  getGridClasses,
  isVisible,
  parseContentAlignmentClass,
  parseHeroOppositeWidth,
  parseHeroWidth,
  toCssLength,
} from "./shared";
import { TextChild } from "./textChild";
import { type WBReveal } from "./reveal";

export function NumbersChild({
  isVisible = true,
  isVisibleNumber,
  textNumber,
  fontSizeNumber = "text-base",
  fontFamilyNumber,
  textAlignNumber = "center",
  fontColorNumber,
  isVisibleDescription,
  textDescription,
  fontSizeDescription = "text-sm",
  fontFamilyDescription,
  textAlignDescription = "center",
  fontColorDescription,
  backgroundColor = "transparent",
  padding = "p-3 wb-md:p-4 wb-lg:p-5",
  distTexts = "gap-2",
  borderRadius,
  border = false,
  borderColor,
  borderWidth = "border",
  borderStyle = "border-solid",
  borderSides = [],
}: {
  isVisible?: boolean;
  isVisibleNumber?: boolean;
  textNumber?: string;
  fontSizeNumber?: string;
  fontFamilyNumber?: string;
  textAlignNumber?: "left" | "center" | "right" | "justify";
  fontColorNumber?: string;
  isVisibleDescription?: boolean;
  textDescription?: string;
  fontSizeDescription?: string;
  fontFamilyDescription?: string;
  textAlignDescription?: "left" | "center" | "right" | "justify";
  fontColorDescription?: string;
  backgroundColor?: string;
  padding?: string;
  distTexts?: string;
  borderRadius?: string;
  border?: boolean;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderSides?: BorderSide[];
}) {
  const { currentRounding, currentColorPalette } = useWBTheme();
  if (!isVisible) return null;
  const roundedClasses = extractUtilityClasses(
    borderRadius ?? currentRounding,
    "rounded",
  );
  const borderClasses = getBorderClasses(
    border,
    borderWidth,
    borderStyle,
    borderSides,
  );
  return (
    <motion.div
      {...CARD_HOVER_PROPS}
      className={classNames(
        "flex flex-col h-full",
        padding,
        distTexts,
        borderClasses,
        roundedClasses,
      )}
      style={{
        transition:
          "background-color 300ms ease-in-out, border-color 300ms ease-in-out",
        backgroundColor:
          backgroundColor !== "transparent" ? backgroundColor : undefined,
        borderColor: borderColor ?? currentColorPalette.secondary,
        borderRadius: roundedClasses
          ? undefined
          : toCssLength(borderRadius ?? currentRounding),
      }}
    >
      <TextChild
        isVisible={isVisibleNumber}
        text={textNumber}
        fontSize={fontSizeNumber}
        fontFamily={fontFamilyNumber}
        textAlign={textAlignNumber}
        color={fontColorNumber}
      />
      <TextChild
        isVisible={isVisibleDescription}
        text={textDescription}
        fontSize={fontSizeDescription}
        fontFamily={fontFamilyDescription}
        textAlign={textAlignDescription}
        color={fontColorDescription}
      />
    </motion.div>
  );
}

export function Numbers(props: RuntimePropsWithChildren) {
  if (!isVisible(props.isVisible as boolean | undefined)) return null;
  const reveal = props.reveal as WBReveal | undefined;
  const padding =
    (props.padding as string | undefined) ?? "p-4 wb-md:p-6 wb-lg:p-8";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";
  const distHeadlineHeader =
    (props.distHeadlineHeader as string | undefined) ?? "mb-2";
  const distHeaderText = (props.distHeaderText as string | undefined) ?? "mb-2";
  const distTextEntries =
    (props.distTextEntries as string | undefined) ?? "gap-3";
  const distEntries = (props.distEntries as string | undefined) ?? "gap-3";
  const layoutWidth =
    (props.layoutWidth as "100%" | "75%" | "50%" | "33%" | "25%" | undefined) ??
    "50%";
  const inversed = (props.inversed as boolean | undefined) ?? false;
  const gridColumns =
    (props.gridColumns as "2" | "3" | "4" | "5" | "6" | undefined) ?? "3";
  const alignmentClass = parseContentAlignmentClass(
    props.contentAlignment as "top" | "center" | "bottom" | undefined,
  );
  const leftWidth = parseHeroWidth(layoutWidth);
  const rightWidth = parseHeroOppositeWidth(layoutWidth);
  return (
    <ThemeSectionContainer
      sectionHeight={props.sectionHeight as number | undefined}
      contentAlignment={
        props.contentAlignment as "top" | "center" | "bottom" | undefined
      }
      backgroundColor={props.backgroundColor as string | undefined}
      padding={padding}
      sectionMargin={sectionMargin}
      border={props.border as boolean | undefined}
      borderColor={props.borderColor as string | undefined}
      borderWidth={props.borderWidth as string | undefined}
      borderStyle={props.borderStyle as string | undefined}
      borderSides={props.borderSides as BorderSide[] | undefined}
      borderRadius={props.borderRadius as string | undefined}
      reveal={reveal}
    >
      <motion.div
        {...SECTION_REVEAL_PROPS}
        className={classNames(
          "items-center max-w-275 flex flex-col w-full",
          alignmentClass,
          (props.isVisibleHeadline as boolean | undefined) ||
            (props.isVisibleHeader as boolean | undefined) ||
            (props.isVisibleText as boolean | undefined)
            ? "gap-5"
            : undefined,
        )}
      >
        <div
          className={classNames(
            "flex w-full",
            distTextEntries,
            layoutWidth !== "100%"
              ? inversed
                ? "flex-col wb-md:flex-row-reverse items-center"
                : "flex-col wb-md:flex-row items-center"
              : inversed
                ? "flex-col-reverse"
                : "flex-col",
          )}
        >
          <div
            className={classNames(
              "flex flex-col grow justify-center",
              leftWidth,
            )}
          >
            <div className={distHeadlineHeader}>
              <Element
                is={TextChild}
                id="headline"
                isVisible={props.isVisibleHeadline as boolean | undefined}
                text={props.textHeadline as string | undefined}
                fontSize={props.fontSizeHeadline as string | undefined}
                textAlign={
                  props.textAlignHeadline as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h2"
              />
            </div>
            <div className={distHeaderText}>
              <Element
                is={TextChild}
                id="header"
                isVisible={props.isVisibleHeader as boolean | undefined}
                text={props.textHeader as string | undefined}
                fontSize={props.fontSizeHeader as string | undefined}
                textAlign={
                  props.textAlignHeader as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
                header
                headingLevel="h1"
              />
            </div>
            <Element
              is={TextChild}
              id="text"
              isVisible={props.isVisibleText as boolean | undefined}
              text={props.textText as string | undefined}
              fontSize={props.fontSizeText as string | undefined}
              textAlign={
                props.textAlignText as
                  | "left"
                  | "center"
                  | "right"
                  | "justify"
                  | undefined
              }
            />
          </div>
          <div
            className={classNames(
              getGridClasses(gridColumns, layoutWidth),
              distEntries,
              rightWidth,
            )}
          >
            {props.children}
          </div>
        </div>
      </motion.div>
    </ThemeSectionContainer>
  );
}
